import { HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AppVersion } from '@ionic-native/app-version';
import { Globalization } from '@ionic-native/globalization';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, App, Events } from 'ionic-angular';
import { Observable } from 'rxjs';
import { Session } from '../../model/myria/app/session';
import { User, IdentityVerification } from '../../model/myria/app/user';
import { SESSION_STATE, DELIVERY_TYPE, KYC_ATTEMPT_STATUS, EVENTS_VALUES, CUSTOMER_SETTINGS, EKYC_PROCESSOR, KYC_METHODS, COUNTRY_SETTINGS, FEATURES, EKYC_PROCESSOR_TYPE } from './app-variables';
import { HttpMapper } from './http-mapper';
import { FeatureFlagTypes, UtilsProvider } from './utils-provider';
import { COUNTRY_CONFIG, APP_CONFIG, Region } from '../../environment';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import * as randomBytes from 'randombytes';
import * as md5 from 'md5';
import { HttpProvider, RefreshUserDetails } from '../http/http';
import { SegmentProvider } from '../analytics/segment';
import { ICustomer } from '../../model/models/customer';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';

declare let FirebasePlugin;
declare let cordova;

@Injectable()
export class LoginProvider {
  private session: Session;
  private selectCustomerId: number;
  private mfaDeviceToken;
  private initPromiseUser;
  private initUserResolve;
  private initUserCall;
  private customer;
  private lastKycResult = null;
  
  // Reuse repeated user reads to avoid duplicate API calls.
  // This is a short-lived reuse window, not a hard cache.
  private lastUserLoadTime: number = 0; // epoch ms of the last successful user load
  private static readonly USER_REFRESH_MS = 2000;

  public walletStatus = "";
  private isFraudBlocked = false;
  public showFraudRestriction: boolean = true;
  public isProfileCallNotRequired: boolean = false;

  public sessionEmitter: EventEmitter<SESSION_STATE> = new EventEmitter<SESSION_STATE>();
  wrongPasscodeEmitter : EventEmitter<any> = new EventEmitter<any>();
  isPINEnabledCountry: boolean = false;

  constructor(
    private alertCtrl: AlertController,
    public app: App,
    private appVersion: AppVersion,
    public events: Events,
    private httpProvider: HttpProvider,
    private globalization: Globalization,
    private http: HttpMapper,
    private translate: TranslateService,
    private utils: UtilsProvider,
    private analytics: SegmentProvider,
    private storageProvider: StorageProvider
  ) {
    this.initSession();
  }

  private getInfoDevice(): Promise<any> {
    return Promise.all([this.globalization.getPreferredLanguage(), this.globalization.getLocaleName(), this.appVersion.getVersionNumber()]);
  }

  initSession() {
    this.session = {
      expires: '',
      issued: '',
      access_token: '',
      expires_in: '',
      refresh_token: '',
      token_type: '',
      userLogin: ''
    };
  }

  buildLoginParams(): Promise<HttpParams> {
    return new Promise((resolve, reject) => {
      let loginData = new HttpParams()
      .append('UserLocalTime', new Date().toISOString());

      this.getInfoDevice().then(
        info => {
          loginData = loginData
            .append('UserLanguage', info[0].value)
            .append('UserLocale', info[1].value)
            .append('AppVersionNo', info[2]);
          resolve(loginData);
        },
        () => {
          loginData = loginData
            .append('AppVersionNo', 'unknown')
            .append('UserLanguage', COUNTRY_CONFIG.DEFAULT_LANG)
            .append('UserLocale', COUNTRY_CONFIG.DEFAULT_LOCALE);
          resolve(loginData);
        }
      );
    });
  }

  appendLoginParams(params: HttpParams, userLogin: string, password: string): HttpParams {
    params = params
    .append('username', userLogin)
    .append('password', password)
    .append('grant_type', 'password');

    return params;
  }

  appendOtpParams(params: HttpParams, securityCode: string, otpCode: string, referenceValue: string): HttpParams {
    params = params
    .append('securityCode', securityCode)
    .append('otp', otpCode)
    .append('referenceValue', referenceValue)
    .append('grant_type', 'otp');

    return params;
  }

  setDataToken(data: any): Promise<any> {
    return new Promise((resolve) => {
      this.session.expires = data['.expires'];
      this.session.issued = data['.issued'];
      this.session.access_token = data.access_token;
      this.session.expires_in = data.expires_in;
      this.session.refresh_token = data.refresh_token;
      this.session.token_type = data.token_type;
      resolve(true)
    });
  }

  async getCustomerAsync(): Promise<any> {
    await this.ensureInitializedUser();
    if (HttpProvider.user && HttpProvider.user.Customers) {
      let custumerId = this.httpProvider.getAuthCustomerId();
      for (let x of HttpProvider.user.Customers) {
        if (x.CustomerId == custumerId) {
          return x;
        }
      }
    }
    return null;
  }

  async getIdentityVerificationAsync(forceReload: boolean = false): Promise<IdentityVerification> {
    await this.ensureInitializedUser(forceReload);
    if (HttpProvider.user) {
      if (HttpProvider.user.IdentityVerification)
        await this.checkKycResultChange(HttpProvider.user.IdentityVerification.KycResult);
      return HttpProvider.user.IdentityVerification;
    }
    return null;
  }

  async getIdentityVerificationsAsync(forceReload: boolean = false): Promise<IdentityVerification[]> {
    await this.ensureInitializedUser(forceReload);
    if (HttpProvider.user) {
      if (HttpProvider.user.IdentityVerifications) {
        const identityVerification = HttpProvider.user.IdentityVerifications.find(e => e.Status == KYC_ATTEMPT_STATUS.VERIFIED)
        if (identityVerification)
          await this.checkKycResultChange(identityVerification.KycResult);
      }
      return HttpProvider.user.IdentityVerifications;
    }
    return null;
  }

  async getUserAsync(forceReload: boolean = false): Promise<User> {
    await this.ensureInitializedUser(forceReload);
    return HttpProvider.user;
  }

  hasUser() {
    if (HttpProvider.user)
      return true;
    return false;
  }

  getToken(): string {
    return this.session.access_token;
  }

  isLiteUser(): boolean {
    return this.httpProvider.getAuthIsLiteUser();
  }

  isProfileCompleted(): boolean {
    return HttpProvider.user && HttpProvider.user.IsProfileCompleted && HttpProvider.user.IsProfileCompleted == true ? true : false;
  }


  async getIsLinkedToCustomer(){
    await this.ensureInitializedUser();
    if (HttpProvider.user)
      return HttpProvider.user.IsLinkedToCustomer;
    return false;
  }

  dateDiff(date: Date, date2: Date): number {
    return date.getTime() - date2.getTime();
  }

  logout(forceLogout?: boolean) {
    this.initSession();
    HttpProvider.user = null;
    this.customer = null;
    this.initPromiseUser = null;
    this.initUserResolve = null;
    this.initUserCall = null;
    this.showFraudRestriction = true;
    this.isProfileCallNotRequired = false;
    this.lastUserLoadTime = 0;
    this.sessionEmitter.emit(this.getSessionState());
    this.utils.setTransactionDotState(false);
    HttpProvider.featureFlags = HttpProvider.featureFlagsCountry;
  }

  getSessionState(): SESSION_STATE {
    let state: SESSION_STATE;
    if (HttpProvider.hasAuthCustomerId()) {
      state = SESSION_STATE.CUSTOMER;
    } else if (this.getToken()) {
      state = SESSION_STATE.LOGGED;
    } else {
      state = SESSION_STATE.NOT_LOGGED;
    }
    return state;
  }

  infoClient(): Promise<User> {
    // Multiple checks using force read to get updated status during sign-in / eKYC-status / Settings / wallet-home entry.
    // Solution: Reuse a recently loaded user to avoid duplicate api/User/Profile calls.
    
    const userLoadedRecently = !!HttpProvider.user && !!this.initPromiseUser
      && (Date.now() - this.lastUserLoadTime) < LoginProvider.USER_REFRESH_MS;

    if (!userLoadedRecently && (!this.initPromiseUser || !this.initUserResolve)) {
      this.initPromiseUser = new Promise((resolve) => {
        this.initUserResolve = resolve;
      });

      const currentUserCall = this.http.get(APP_CONFIG.API_URL + 'api/User/Profile')
      this.initUserCall = currentUserCall;
      this.initUserCall.subscribe(
        user => {
          if (this.initUserCall !== currentUserCall) // wait for the last call because timing could be cruicial
            return this.initPromiseUser;

          this.setUser(user);
        },
        err => {
          if (err.status === 401 || err.status === 403) // these are already handled and shouldn't cause a reload
            this.initPromiseUser = null;
          else
            window.setTimeout(() => {
              this.infoClient();
            }, 10000);
        }
      );
    }
    return this.initPromiseUser;
  }

  getKycStatus(forceReload: boolean = true): Promise<any> {
    return new Promise(async (resolve, reject) => {
      var identityVerification;
      if (await this.hasMultipleEkycProviders()) {
        var identityVerifications = await this.getIdentityVerificationsAsync();
        identityVerification = identityVerifications.find(e => e.Status == KYC_ATTEMPT_STATUS.VERIFIED)
        if (identityVerification)
          resolve({ isFirstTime: false, attempt: identityVerification.Status, kycResult: identityVerification.KycResult, attemptsLeft: identityVerification.AttemptsLeft });
      }
      identityVerification = await this.getIdentityVerificationAsync(forceReload);
      if(identityVerification.Status == KYC_ATTEMPT_STATUS.NOT_ATTEMPTED) {
        resolve({isFirstTime: true})
      }
      else {
        const lastAttempts = identityVerification.Status;
        resolve({isFirstTime: false, attempt: lastAttempts, kycResult: identityVerification.KycResult, attemptsLeft: identityVerification.AttemptsLeft});
      }
    });
  }

  async checkKycResultChange(kycResult) {
    // when KYC status has changed, refresh the token since eventually customer linking has changed the customer ID
    if (this.lastKycResult && this.lastKycResult !== kycResult) {
      LoggerProvider.info(this, 'Refresh because KYC result has been changed from ' + this.lastKycResult + ' to ' + kycResult);
      await this.httpProvider.refresh(false, true, RefreshUserDetails.Basic); // include user details so backend will query user and get latest customer ID
    }
    this.lastKycResult = kycResult;
  }

  async getUserSettingAsync(type: number): Promise<any> {
    await this.ensureInitializedUser();
    if (HttpProvider.user) {
      for (let setting of HttpProvider.user.Settings) {
        if (setting.SettingType == type) {
          return setting.SettingValue;
        }
      }
    }
    return null;
  }

  async getCustomerSettingAsync(type: number): Promise<any> {
    await this.ensureInitializedUser();
    if (HttpProvider.user && HttpProvider.user.Customers && HttpProvider.user.Customers.length > 0 && HttpProvider.user.Customers[0].Settings) {
      for (let customerSetting of HttpProvider.user.Customers[0].Settings) {
        if (customerSetting.SettingType == type) {
          return customerSetting.SettingValue;
        }
      }
    }
    return null;
  }

  async getUserName(){
    await this.getUserAsync(); // ensure user is initialized
    return (HttpProvider.user && HttpProvider.user.LoginName) ? HttpProvider.user.LoginName : null;
  }

  ensureInitializedUser(forceReload: boolean = false) {
    if (forceReload || !this.initPromiseUser)
      this.infoClient();

    if (this.initPromiseUser)
      return this.initPromiseUser;
    return null;
  }

  forgotPasswordResetPassword(loginName: string, password: string, code?: string, OtpActionRequest?: any): Observable<any> {
    let data = {};
    data['LoginName'] = loginName;
    data['ResetCode'] = code;
    data['NewPassword'] = password;
    data['OtpActionRequest'] = OtpActionRequest;

    return this.http.post(APP_CONFIG.API_URL + 'api/Account/ResetPassword', data);
  }

  updateSetting(SettingArea: number, SettingType: number, SettingValue: string | number): Observable<any> {
    return this.http.post(APP_CONFIG.API_URL + 'api/User/Setting', {
      SettingType: SettingType,
      SettingArea: SettingArea,
      NewSettingValue: SettingValue
    });
  }

  updateCustomerSetting(SettingArea: number, SettingType: number, SettingValue: string | number, customerId: number): Observable<any> {
    return this.http.post(APP_CONFIG.API_URL + 'api/Customer/' + customerId + '/Setting', {
      SettingType: SettingType,
      SettingArea: SettingArea,
      NewSettingValue: SettingValue
    });
  }

  validateOtp(securityCode: string, otpCode: string, referenceValue: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.buildLoginParams().then(
        params => {
          let loginData = this.appendOtpParams(params, securityCode, otpCode, referenceValue);

          this.http.post(APP_CONFIG.API_URL + 'Token', loginData).subscribe(
            tokenData => resolve(tokenData),
            err => reject(err)
          );
        }
      );
    });
  }

  async resendOtp(action: number, securityCode: string, referenceValue: string, deliveryType: number,loginName:string, sessionKey?: any, fraudOtpPreOrderId: number = undefined): Promise<any> {
    const mfaDeviceToken = await this.getMfaDeviceToken();
    return new Promise((resolve, reject) => {
      this.http.post(APP_CONFIG.API_URL + 'api/OTP', {
        securityCode: securityCode,
        referenceValue: referenceValue,
        loginName: loginName,
        action: action,
        sessionKey: sessionKey,
        'deliveryType': deliveryType,
        'deviceId': LoggerProvider.uuid,
        'DeviceSimCard': mfaDeviceToken,
        'FraudOtpPreOrderId': fraudOtpPreOrderId
      }).subscribe(response => resolve(response), err => reject(err));
    });
  }

  public async getMfaDeviceToken() {
    let mfaDeviceToken = await this.storageProvider.get(StorageType.MfaDeviceToken);
    if (!mfaDeviceToken) {
      // it seems that on a few devices the storage is not working correclty hence it would generate new mfaDeviceTokens every time the function is called
      // and hence it would break the login OTP -> store it also in memory when it has been generated and use it there
      if (this.mfaDeviceToken)
        return this.mfaDeviceToken;

      mfaDeviceToken = LoggerProvider.sessionId + randomBytes(16);
      mfaDeviceToken = md5(mfaDeviceToken);
      this.mfaDeviceToken = mfaDeviceToken;
      this.storageProvider.set(StorageType.MfaDeviceToken, mfaDeviceToken);
    }
    return mfaDeviceToken;
  }

  async passSecurityQuestionsWithOtp(username: string, deliveryType: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(APP_CONFIG.API_URL + 'api/auth/ByPassSecurityQuestionsByOtp', {
        'userName': username,
        'deliveryType': deliveryType,
      }).subscribe(response => resolve(response), err => reject(err));
    });
  }

  async validateSecurityQuestionsByOtp(referenceValue: string, action: number, OTPCode: string, deliveryType: number, securityCode: string = null): Promise<any> {
    const mfaDeviceToken = await this.getMfaDeviceToken();
    let data = {
      referenceValue: referenceValue,
      'action': action,
      'code': OTPCode,
      "DeviceId": LoggerProvider.uuid, // is like this.device.uuid but also supports a virtual uuid for browserss
      "DeviceSimCard": mfaDeviceToken,
      'DeliveryType': deliveryType,
      'SecurityCode': securityCode,
    };
    let url = APP_CONFIG.API_URL + 'api/auth/V2/ValidateSecurityQuestionsByOtp';
    if (APP_CONFIG.featureFlags.signinOld)
      url = APP_CONFIG.API_URL + 'api/auth/ValidateSecurityQuestionsByOtp';
    return this.http.post(url, data).toPromise();
  }

  // Non-success is a 422 carrying the RetVal and block state, returned as data
  async verifyBirthdateForLogin(referenceValue: string, birthDate: string): Promise<any> {
    try {
      return await this.http.post(APP_CONFIG.API_URL + 'api/Auth/v3/VerifyBirthdateForLogin', {
        'ReferenceValue': referenceValue,
        'BirthDate': birthDate,
      }).toPromise();
    } catch (ex) {
      if (ex && ex.status === 422 && ex.error && ex.error.Result)
        return ex.error;
      throw ex;
    }
  }

  getKYCProcessor(number) {
    return EKYC_PROCESSOR[number] != undefined ?EKYC_PROCESSOR[number] : "unknown_provider";
  }

  getKYCProcessorNumber(processor): Number | null {
    const num = Object.keys(EKYC_PROCESSOR).find(key => EKYC_PROCESSOR[key] === processor);
    return num ? Number(num) : null;
  }

  async identifyLiteCustomer(user, setMarketing, skipMarketing, registrationMarketingOptIn: boolean = null) {
    let additionalTraits = {};
    // Do not set an ID here. However, keep using this method to add more traits to
    // the given anonymous user so we have that linked data when the user reaches Account Creation Complete.
    if (user && user.IsMigrated) {
      additionalTraits["is_migrated"] = user.IsMigrated
    }
    let liteUser: any = {
      id: null,
      emailAddress: this.httpProvider.getAuthEmail()
    }
    if (registrationMarketingOptIn !== null) {
      const consent = await this.getLiteRegistrationConsent(registrationMarketingOptIn);
      if (consent)
        liteUser = { ...liteUser, ...consent };
    }
    if (user && user.LastVerifiedPhone && user.LastVerifiedPhone.CountryCode) {
      additionalTraits = {
        ...additionalTraits,
        phoneNumber: '+' + user.LastVerifiedPhone.CountryCode + user.LastVerifiedPhone.Number,
        phoneCountry: user.LastVerifiedPhone.CountryCode
      };
    }
    this.analytics.identifyLiteCustomer(liteUser, setMarketing, skipMarketing, additionalTraits);
  }

  // A lite user has no profile to read the consent model from, so at registration it is
  // derived like RMT5 web does it: soft opt-in market (country setting 509) unless the user opted out.
  async getLiteRegistrationConsent(marketingOptIn: boolean): Promise<{ marketingConsentType: string, legitimateInterest: boolean }> {
    let softOptInMarket: boolean;
    try {
      softOptInMarket = await this.utils.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.SHOW_SOFT_OPTIN) === '1';
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to resolve the soft opt-in setting for the lite identify');
      return null;
    }
    const soft = softOptInMarket && marketingOptIn;
    return {
      marketingConsentType: soft ? 'soft' : 'express',
      legitimateInterest: soft,
    };
  }

  async identifyCustomer(customer) {
    this.customer = customer;
    let traits: any = {
      userRegType: this.httpProvider.getAuthIsLiteUser() ? 'Lite' : 'Full'
    };

    if (HttpProvider.user) {
      let verificationFailed = false;
      let verifiedProcessors = new Array();
      const identityVerifications = await this.getIdentityVerificationsAsync();

      identityVerifications.forEach(e => {
        if (e.Status == KYC_ATTEMPT_STATUS.VERIFIED) {
          verifiedProcessors.push(this.getKYCProcessor(e.Processor));
        }

        if (e.Status == KYC_ATTEMPT_STATUS.FAIL) {
          verificationFailed = true;
        }
      });

      if(verifiedProcessors.length > 0) {
          traits = {
            ...traits,
            isEKYC: true,
            EKYCProcessor: verifiedProcessors
          };
        } else if (verificationFailed) {
          traits = { ...traits, isEKYC: false };
        }  else {
          traits = { ...traits, EKYCProcessor: verifiedProcessors, isEKYC: false };
        }

      let referFriend = false; // TODO CHECK
      traits = {
        ...traits,
        referralCode: (referFriend) ? await this.getCustomerSettingAsync(CUSTOMER_SETTINGS.RAF_REFERRAL_CODE.TYPE) : null,
        newCustomerReferral: (referFriend && HttpProvider.user && HttpProvider.user.Customers && HttpProvider.user.Customers.length > 0) ? HttpProvider.user.Customers[0].RAFCustomerIsReferred : null,
        promoType: (referFriend) ? EVENTS_VALUES.RAF : null,
        referralUrl: (referFriend) ? await this.getCustomerSettingAsync(CUSTOMER_SETTINGS.RAF_REFERRAL_URL.TYPE) : null,
        is_migrated: HttpProvider.user.IsMigrated

      }

      if(this.walletStatus !== ""){
        traits = { ...traits, walletStatus: this.walletStatus };
      }
    }
    this.analytics.identifyCustomer(this.customer, traits);
  }

  getUserUnsafe(){
    if(APP_CONFIG.featureFlags.signinOld) return HttpProvider.user;
    return null;
  }

  // for those countries which have a fallback ekyc and thus we need to use IdentityVerificationS from the /User api
  async hasMultipleEkycProviders() {
    if (this.isMultipleEkycProviderCountry()) {
      try {
        if (APP_CONFIG.region.region === Region.NZ)
          return true; // NZ needs to go through both Veriff and TN thus doesn't have a 'fallback' country setting per se, nevertheless we treat it like it does as we still want it to use /User.IdentityVerificationS
        const fallbackSetting = await this.utils.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.FALLBACK_EKYC_METHOD);
        if (fallbackSetting == KYC_METHODS.SIGNICAT || fallbackSetting == KYC_METHODS.VERIFF || fallbackSetting == KYC_METHODS.ONFIDO)
          return true;
        const higherLimitSetting = await this.utils.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.HIGHER_LIMIT_EKYC_PROCESSOR);
        if (higherLimitSetting == KYC_METHODS.VERIFF || higherLimitSetting == KYC_METHODS.ONFIDO)
          return true;
      }
      catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Error while getting Signicat status');
      }
    }
    return false;
  }

  isMultipleEkycProviderCountry() {
    if (APP_CONFIG.region.region == Region.UK ||
      APP_CONFIG.region.region == Region.AU ||
      APP_CONFIG.region.region == Region.US ||
      APP_CONFIG.region.region == Region.CA ||
      APP_CONFIG.region.region == Region.NZ ||
      APP_CONFIG.region.region == Region.CL ||
      this.isSignicatCountry())
      return true;
    else return false;
  }

  isSignicatCountry() {
    if (APP_CONFIG.region.region == Region.SE || APP_CONFIG.region.region == Region.DK || APP_CONFIG.region.region == Region.NO)
      return true;
    else return false;
  }

  setUser(user) {
    const previousUserResolve = this.initUserResolve


    if (user) {
      this.isFraudBlocked = user.IsFraudBlocked;
      this.initPromiseUser = new Promise((resolve) => {
        this.initUserResolve = resolve;
      });

      let email = this.httpProvider.getAuthEmail();
      if (email) {
        email = email.toLowerCase();
        // TODO !!!!!!!!!!!!!!!!!!!!!!!!!! temporary
        let flags = {};
        if (email.indexOf('@epay.de') >= 0 ||
            (email.indexOf('@riamoneytransfer.com') >= 0 && email.indexOf('piel') >= 0)) {
          flags = {
            ...flags,
            [FeatureFlagTypes.HomescreenShowCarrousel]: true,
            [FeatureFlagTypes.HomescreenShowRecentView]: true,
            [FeatureFlagTypes.StoreLocatorEmbedded]: true,
          };
        }

        if(APP_CONFIG.featureFlags.showNewHomePage && APP_CONFIG.region.showNewHomePage) {
          flags = {
            ...flags,
            [FeatureFlagTypes.HomescreenShowCarrousel]: true,
            [FeatureFlagTypes.HomescreenShowRecentView]: true,
            [FeatureFlagTypes.HomescreenShowRecentRate]: false,
          };
        }
        if (email.indexOf('khanusa74') >= 0 && APP_CONFIG.API_URL.indexOf('https://qa') === 0) {
          LoggerProvider.info(this, '');
          flags = {
            ...flags,
            [FeatureFlagTypes.HomescreenRecentRecipientsEndpoint]: true
          };
          const customer = this.getUserCustomer(user);
          if (customer &&
              typeof(customer.WillReceivePushMarketing) === 'undefined' &&
              typeof(customer.WillReceivePushNotifications) === 'undefined') {
            LoggerProvider.info(this, 'Test set WillReceivePushMarketing');
            customer.WillReceivePushMarketing = false;
            customer.WillReceivePushNotifications = false;
          }
        }
      }
    }
    HttpProvider.user = user; // user has been moved to HttpProvider since we also get it from refresh
    if (user)
      this.lastUserLoadTime = Date.now(); // stamp for infoClient refresh

    // if we have a customer update the identify customer with the user information (try to avoid unnecessary calls to the user endpoint just for additional identify traits since the data shouldn't change often)
    // REMEMBER: once identify traits have been supplied we don't have to resupply them until they have actually changed
    if (this.isLiteUser())
      this.identifyLiteCustomer(HttpProvider.user, false, true);
    else if (this.customer)
      this.identifyCustomer(this.customer);

    this.sessionEmitter.emit(this.getSessionState());
    if (this.initUserResolve)
      this.initUserResolve(HttpProvider.user);
    if (previousUserResolve)
      previousUserResolve(HttpProvider.user);
    this.initUserResolve = null;
    if (user && !user.Settings.some(s => s.SettingType == 20 && s.SettingValue)) {
      const settingArea = 1;
      const settingValue = 20;
      this.updateSetting(settingArea, settingValue, COUNTRY_CONFIG.PREFERRED_COUNTRY).subscribe(() => this.infoClient(), () => {
        LoggerProvider.error(this, LoggerEvent.ProviderException, 'Failed to update preferred country setting');
      });
    }
  }

  async setIsPINEnabledCountry(){
    const isPinLoginCountry = await this.utils.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.PIN_LOGIN_ENABLED);
    // Passcode-only login is restricted to SN as of now as US and other brand service  (viva, miranda, wmt) having issues 
    const isSN = APP_CONFIG.region.region === Region.SN;
    this.isPINEnabledCountry = isSN && isPinLoginCountry == 1;
  }

  identifyUser() {
    const userCustomer = this.getUserCustomer();
    if (!userCustomer) {
      LoggerProvider.error(this, LoggerEvent.ProviderException, 'Identify user failed because of missing customer !');
      return;
    }

    const customer: ICustomer = {
      id: userCustomer.CustomerId,
      emailAddress: userCustomer.EmailAddress ? userCustomer.EmailAddress : '',

      willReceivePushMarketing: userCustomer.WillReceivePushMarketing ? userCustomer.WillReceivePushMarketing : false, // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      willReceiveEmailMarketing: userCustomer.WillReceiveEmailMarketing ? userCustomer.WillReceiveEmailMarketing : false,
      willReceivePushNotifications: userCustomer.WillReceivePushNotifications ? userCustomer.WillReceivePushNotifications : false, // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      willReceiveEmailNotifications: userCustomer.willReceiveEmailNotifications ?  userCustomer.willReceiveEmailNotifications : false,
      willReceiveServiceEmailMessages: userCustomer.willReceiveServiceEmailMessages ? userCustomer.willReceiveServiceEmailMessages : false,
      willReceiveServicePushMessages: userCustomer.willReceiveServicePushMessages ? userCustomer.willReceiveServicePushMessages : false,
      adsTracking: userCustomer.adsTracking ? userCustomer.adsTracking : false,

      firstName: userCustomer.FirstName,
      lastName: userCustomer.FirstLastName,
      stateName: userCustomer.StateName ? userCustomer.StateName : null,
      dateOfBirth: userCustomer.DateOfBirth ? userCustomer.DateOfBirth : '',
      address: {
        country: userCustomer.Country ? userCustomer.Country : '',
        city: userCustomer.City ? userCustomer.City : '',

        // TODO !!!!!!!!!!!!!!!!!!!!!!!!! Unused for the moment - should be also filled
        type: '',
        street: '',
        stateProvince: '',
        postCode: ''
      },
      isDisabled: false,

      // TODO !!!!!!!!!!!!!!!!!!!!!!!!! Unused for the moment - should be also filled
      middleName: '',
      lastName2: '',
      homePhone: {
        type: '',
        areaCode: '',
        countryCode: '',
        number: '',
        phoneNumberFormatted: '',
        isValid: false
      },
      mobilePhone: {
        type: '',
        areaCode: '',
        countryCode: '',
        number: '',
        phoneNumberFormatted: '',
        isValid: false
      },
      cultureCode: '',
      hasTaxId: false,
      nationality: '',
      countryOfBirth: '',
      occupation: '',
      kycStatus: {
        canEKyc: true,
        profileActionsRequired: [],
        canTransact: true,
        kycFailed: false,
        restrictedCountryTo: []
      },
      identification: {
        manualApprovalFound: false,
        idVerificationRequired: false,
      },
      additionalAddresses: [],
    };

    if (HttpProvider.user.LastVerifiedPhone) {
      customer.mobilePhone = {
        countryCode: HttpProvider.user.LastVerifiedPhone.CountryCode,
        number: HttpProvider.user.LastVerifiedPhone.Number,
        phoneNumberFormatted: HttpProvider.user.LastVerifiedPhone.FormattedNumber,
        type: HttpProvider.user.LastVerifiedPhone.Type,
        areaCode: '',
        isValid: HttpProvider.user.LastVerifiedPhone.Verified,
      };
    } else {
      LoggerProvider.error(this, LoggerEvent.ProviderException, 'Identify user incomplete because last verified phone number is missing !');
    }

    this.identifyCustomer(customer);
  }

  getUserCustomer(user = HttpProvider.user) {
    if (user && user.Customers && user.Customers.length > 0)
      return user.Customers[0];
    return null;
  }

  getUserCustomerIdentification(user = HttpProvider.user) {
    const customer = this.getUserCustomer(user);
    if (customer && customer.Identifications && customer.Identifications.length > 0){
      let defaultIdentifications = customer.Identifications.find((item) => item.Default == true)
      return defaultIdentifications;
    }
    return null;
  }

  async isCustomerIDInvalid() {
    const today = new Date();
    // Users verified by Signicat do not populate our identification data object
    const isSignicatVerified = await this.getIdentityVerificationFromCustomer(EKYC_PROCESSOR_TYPE.Signicat)
    if (!isSignicatVerified) {
      const identification = this.getUserCustomerIdentification();
      if (!isSignicatVerified && identification && identification.ExpirationDate) {
        if (new Date(identification.ExpirationDate.toString()).getTime() < new Date(today).getTime())
        return true;
      }
    }
    return false;
  }

  // returns true when the ID is still valid but expires within days (default 30)
  async isCustomerIDExpiringSoon(days: number = 30) {
    // for expired/invalid IDs are handled by isCustomerIDInvalid() then skip the pre-expiry check.
    if (await this.isCustomerIDInvalid()) return false;
    const identification = this.getUserCustomerIdentification();
    if (!identification || !identification.ExpirationDate) return false;
    const expiry = new Date(identification.ExpirationDate.toString()).getTime();
    const now = new Date().getTime();
    const threshold = now + (days * 24 * 60 * 60 * 1000);
    return expiry >= now && expiry <= threshold;
  }

  // returns true if processor is verified
  async getIdentityVerificationFromCustomer(identityVerificationType: EKYC_PROCESSOR_TYPE | EKYC_PROCESSOR_TYPE[]) {
    let statusVerified = false;
    try {
      const identification = this.getUserCustomerIdentification();
      if (HttpProvider.user && HttpProvider.user.IdentityVerifications) {
        if(typeof identityVerificationType === 'string'){
          const kycProcessorFiltered = HttpProvider.user.IdentityVerifications.filter((obj) => {
            return obj.Processor === identityVerificationType;
          });
          if (kycProcessorFiltered.length > 0) {
            const status = kycProcessorFiltered[0].Status;
            statusVerified = status === KYC_ATTEMPT_STATUS.VERIFIED;
          }
        }
        else {
          /**
           * since we are adding veriff to US users, but also according to requirement we need to also check for onfido, for users who
           * have onfido verified. Therefore in the else, we are sending an array containing verrif and onfido, then looping and checking if the user
           * identityverificationprocess contains either veriff or onfido. If user contains onfido and it is verified then we break and return from the function and same thing
           * if user contains veriff and verified then we break and return from the function.
           */
          const identityVerificationTypes: EKYC_PROCESSOR_TYPE[] = identityVerificationType as EKYC_PROCESSOR_TYPE[];
          for(let type of identityVerificationTypes) {
            const kycProcessorFiltered = HttpProvider.user.IdentityVerifications.filter((obj) => {
              return obj.Processor === type;
            });
            if (kycProcessorFiltered.length > 0) {
              const status = kycProcessorFiltered[0].Status;
              statusVerified = status === KYC_ATTEMPT_STATUS.VERIFIED;
              if(statusVerified) break;
            }
          }
        }
      }
    } catch (ex) {
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.KYCVerificationError");
    }

    return statusVerified;
  }

  isUserFraudBlocked() {
    return this.isFraudBlocked;
  }

  async getUserSetting(type){
    const user = await this.getUserAsync();
    if(user && user.Settings){
      let userSetting = user.Settings.find(setting => setting.SettingType === type);
      return userSetting && userSetting.SettingValue ? userSetting.SettingValue : null
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/myria/login-provider.ts